/**
 * A mentor's own diary: when they work, and when they do not.
 *
 * Ported from the app's `mentor_availability_screen.dart` and
 * `widgets/mentor_break_sections.dart`.
 *
 * *** NOTHING HERE CANCELS A BOOKING ***
 * Adding a break or a vacation never voids a settled session on its own
 * (ARCHITECTURE §6.3, Race 3). The mentor is shown exactly what would clash
 * and decides about those separately — which is why cancelling is its own
 * endpoint with its own confirmation, and not a checkbox on the save.
 *
 * *** TWO GATES ON EVERY DAY ***
 * `operating_days` is the TENANT's week. Hours set on a day the institute
 * is closed generate no slots at all, so those rows say so rather than
 * letting a mentor configure something inert.
 */

import { useState } from 'react'
import { PageHeader, PageStack, Card } from '@/components/ui/Page'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  useMyBreaks,
  useMyOneOffBreaks,
  useMyVacations,
  useMyWorkingHours,
} from '@/lib/queries'
import { WorkingHoursSection } from './WorkingHoursSection'
import { BreaksSection } from './BreaksSection'
import { TimeOffSection } from './TimeOffSection'
import styles from './AvailabilityPage.module.css'

export function AvailabilityPage() {
  const { user } = useAuth()
  const isMentor = user?.role === 'MENTOR'

  /*
    *** ONLY A MENTOR HAS A DIARY ***
    Every endpoint on this page lives under `/mentor` and answers 403 for
    anyone else. Said plainly rather than firing four requests that fail.
  */
  const hours = useMyWorkingHours(isMentor)
  const breaks = useMyBreaks(isMentor)
  const oneOff = useMyOneOffBreaks(isMentor)
  const vacations = useMyVacations(isMentor)

  const [tab, setTab] = useState<'hours' | 'breaks' | 'timeoff'>('hours')

  if (!isMentor) {
    return (
      <PageStack>
        <PageHeader title="Availability" />
        <Card>
          <p className={styles.muted}>
            This page is for mentors. An administrator sets a mentor&rsquo;s
            hours from the Mentors screen.
          </p>
        </Card>
      </PageStack>
    )
  }

  const error = hours.error ?? breaks.error ?? oneOff.error ?? vacations.error

  /*
    One flag for the whole feature: the super admin switches breaks,
    one-off breaks and vacations on together, and both endpoints report it.
  */
  const breaksEnabled = oneOff.data?.enabled ?? vacations.data?.enabled ?? true

  const tabs = [
    { id: 'hours' as const, label: 'Weekly hours' },
    { id: 'breaks' as const, label: 'Breaks' },
    { id: 'timeoff' as const, label: 'Time off' },
  ]

  return (
    <PageStack>
      <PageHeader
        title="Availability"
        description="When students can book you."
      />

      {error && (
        <p className={styles.error}>
          {error instanceof ApiError
            ? error.message
            : 'Could not load your availability.'}
        </p>
      )}

      <div className={styles.tabs} role="tablist" aria-label="Availability">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`${styles.tab} ${tab === t.id ? styles.tabOn : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'hours' && (
        <WorkingHoursSection
          data={hours.data ?? null}
          loading={hours.isLoading}
        />
      )}

      {tab === 'breaks' && (
        <BreaksSection
          enabled={breaksEnabled}
          recurring={breaks.data?.breaks ?? null}
          oneOff={oneOff.data?.breaks ?? null}
          timezone={hours.data?.timezone ?? null}
          loading={breaks.isLoading || oneOff.isLoading}
        />
      )}

      {tab === 'timeoff' && (
        <TimeOffSection
          enabled={breaksEnabled}
          vacations={vacations.data?.vacations ?? null}
          loading={vacations.isLoading}
        />
      )}
    </PageStack>
  )
}
